"use client";

import React from "react";
import { Badge } from "@/components/ui/badge";
import { Shield, Zap, MapPin, Clock, CheckCircle } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface SquadSpecsBarProps {
  /** Average response time in minutes. If null, shown as "On Call" */
  responseTimeMinutes?: number | null;
  /** Verification level: "Basic Listed", "Verified Handyman", "Verified Specialist", "Licensed Pro" */
  verificationLevel?: string;
  /** Insurance status and max coverage (e.g., "Insured up to R1m") */
  insuranceStatus?: string;
  /** Emergency availability: "24-7", "after-hours", "business-hours", or null */
  emergencyAvailability?: string | null;
  /** Service radius in km */
  serviceRadiusKm?: number;
  /** Is available/on-call right now */
  isAvailableNow?: boolean;
  /** Show compact version for cards; full version for profile pages */
  compact?: boolean;
}

export function SquadSpecsBar({
  responseTimeMinutes,
  verificationLevel,
  insuranceStatus,
  emergencyAvailability,
  serviceRadiusKm,
  isAvailableNow,
  compact = false,
}: SquadSpecsBarProps) {
  const specs = [];

  // Response time or on-call status
  if (isAvailableNow) {
    specs.push({
      icon: <Zap size={14} className="text-green-600" />,
      label: "Available Now",
      value: responseTimeMinutes ? `${responseTimeMinutes} min` : "On Call",
      tooltip: responseTimeMinutes
        ? `Usually arrives within ${responseTimeMinutes} minutes`
        : "Ready to accept jobs right now",
    });
  } else if (responseTimeMinutes) {
    specs.push({
      icon: <Clock size={14} className="text-blue-600" />,
      label: "Squad Response",
      value: `~${responseTimeMinutes} min`,
      tooltip: `Average response time: ${responseTimeMinutes} minutes`,
    });
  }

  // Verification level
  if (verificationLevel) {
    specs.push({
      icon: <CheckCircle size={14} className="text-amber-600" />,
      label: "Verification",
      value: verificationLevel,
      tooltip: `Verification level: ${verificationLevel}`,
    });
  }

  // Insurance
  if (insuranceStatus) {
    specs.push({
      icon: <Shield size={14} className="text-purple-600" />,
      label: "Insurance",
      value: insuranceStatus,
      tooltip: `Squad Protection: This pro is covered for ${insuranceStatus}`,
    });
  }

  // Service radius
  if (serviceRadiusKm) {
    specs.push({
      icon: <MapPin size={14} className="text-red-600" />,
      label: "Coverage",
      value: `${serviceRadiusKm} km`,
      tooltip: `Service radius: up to ${serviceRadiusKm} km`,
    });
  }

    // Availability window
  if (emergencyAvailability) {
    const availLabels: Record<string, string> = {
      "24-7": "Anytime",
      "after-hours": "After hours",
      "business-hours": "Business hours",
    };
    specs.push({
      icon: <Zap size={14} className="text-orange-600" />,
      label: "Available",
      value: availLabels[emergencyAvailability] || emergencyAvailability,
      tooltip: `Availability window: ${availLabels[emergencyAvailability] || emergencyAvailability}`,
    });
  }

  if (specs.length === 0) {
    return null;
  }

  if (compact) {
    // Compact card version: show 2-3 key specs as horizontal badges
    return (
      <div className="flex flex-wrap gap-1.5">
        {specs.slice(0, 3).map((spec, idx) => (
          <TooltipProvider key={idx}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="secondary" className="text-xs px-2 py-1 flex items-center gap-1 cursor-help">
                  {spec.icon}
                  <span>{spec.value}</span>
                </Badge>
              </TooltipTrigger>
              <TooltipContent className="text-xs">{spec.tooltip}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ))}
      </div>
    );
  }

  // Full version: show all specs in a grid
  return (
    <div className="rounded-lg border border-[#d9e4f7] bg-[#f7f9fd] p-4">
      <p className="text-xs font-semibold text-[#214186] uppercase tracking-wide mb-3">Squad Specs</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {specs.map((spec, idx) => (
          <TooltipProvider key={idx}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex flex-col items-start gap-1 p-2 rounded border border-blue-200 bg-white cursor-help hover:bg-blue-50 transition">
                  <div className="flex items-center gap-1">
                    {spec.icon}
                    <span className="text-xs font-semibold text-[#0077B6]">{spec.label}</span>
                  </div>
                  <span className="text-sm font-bold text-slate-900">{spec.value}</span>
                </div>
              </TooltipTrigger>
              <TooltipContent className="text-xs max-w-xs">{spec.tooltip}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ))}
      </div>
    </div>
  );
}
